import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const OUTCOME_BG = {
  Verified: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Conflict: "bg-red-50 text-red-700 border-red-200",
  "Missing required fields": "bg-amber-50 text-amber-700 border-amber-200",
};

function ConfirmDialog({ onClose, onConfirm, busy }) {
  const [returnTo] = useState(() => document.activeElement);
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="srcval-confirm-dialog" aria-busy={busy}
        onCloseAutoFocus={(e) => { e.preventDefault(); returnTo?.focus?.(); }}>
        <DialogHeader>
          <DialogTitle className="font-heading text-base">Reclassify all sources</DialogTitle>
          <DialogDescription className="text-xs text-slate-600 leading-snug">
            The objective validation service will re-validate every source against its class rules (journal, congress,
            internal, external), replace each verification status with the objective result, suspend synthesis approval
            for sources that fail, refresh the confidence index and file an exception report. This is logged and cannot
            be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="h-8 text-xs" disabled={busy} onClick={onClose} data-testid="srcval-cancel-btn">Cancel</Button>
          <Button className="h-8 text-xs bg-slate-900 text-white hover:bg-slate-800" disabled={busy} onClick={onConfirm} data-testid="srcval-confirm-btn">
            {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" aria-hidden="true" />}{busy ? "Reclassifying…" : "Run reclassification"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const SourceValidation = ({ onReclassified }) => {
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [openPanel, setOpenPanel] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoadError(false);
    api.get("/source-validation").then((r) => setStatus(r.data)).catch(() => setLoadError(true));
  };
  useEffect(load, []);

  const run = () => {
    setBusy(true);
    api.post("/source-validation/run")
      .then((r) => {
        load(); setConfirming(false); setOpenPanel(true);
        toast.success(`${r.data.run_id}: ${r.data.summary.verified}/${r.data.summary.sources} sources objectively verified — ${r.data.exceptions.length} exception(s) filed.`);
        onReclassified?.();
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Reclassification failed — please retry.",
        { action: { label: "Retry", onClick: run } }))
      .finally(() => setBusy(false));
  };

  const rep = status?.latest;
  return (
    <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm" data-testid="source-validation-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <button onClick={() => setOpenPanel(!openPanel)} aria-expanded={openPanel}
          aria-label={`${openPanel ? "Collapse" : "Expand"} objective source validation panel`}
          className="flex items-start gap-2 text-left" data-testid="srcval-toggle">
          {openPanel ? <ChevronDown className="w-4 h-4 text-slate-400 mt-0.5" aria-hidden="true" /> : <ChevronRight className="w-4 h-4 text-slate-400 mt-0.5" aria-hidden="true" />}
          <ShieldCheck className="w-4 h-4 text-teal-600 mt-0.5" aria-hidden="true" />
          <div>
            <h3 className="font-heading text-sm font-bold text-slate-900">Objective source validation</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              'Source verified' is assigned only by the validation service — manual verification is rejected when required fields are missing or conflicting.
              {rep && <span className="ml-1 font-semibold text-slate-700" data-testid="srcval-summary-inline">Last run: {rep.summary.verified}/{rep.summary.sources} verified · {rep.exceptions.length} exceptions.</span>}
            </p>
          </div>
        </button>
        <Button className="h-8 text-xs bg-slate-900 text-white hover:bg-slate-800" onClick={() => setConfirming(true)}
          data-testid="srcval-run-btn" aria-label="Run objective reclassification of all sources">
          {rep ? "Re-run reclassification" : "Reclassify all sources"}
        </Button>
      </div>

      {!status && !loadError && (
        <p className="text-[11px] text-slate-400 flex items-center gap-1.5 mt-2" role="status" aria-live="polite" data-testid="srcval-loading">
          <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />Loading validation status…
        </p>
      )}
      {loadError && (
        <p className="text-[11px] text-red-600 flex items-center gap-2 mt-2" role="alert" data-testid="srcval-load-error">
          Validation status could not be loaded.
          <Button variant="outline" className="h-6 text-[10px]" onClick={load} data-testid="srcval-retry-btn">Retry</Button>
        </p>
      )}

      {openPanel && status && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2" data-testid="srcval-rules">
            {status.rules.map((r) => (
              <div key={r.class} className="border border-slate-200 rounded-lg px-3 py-2">
                <p className="text-[10.5px] font-bold text-slate-800">{r.class}</p>
                <p className="text-[10px] text-slate-500 leading-snug mt-0.5">{r.requirements}</p>
              </div>
            ))}
          </div>
          {!rep && <p className="text-xs text-slate-500" data-testid="srcval-never-run">No reclassification run on record yet.</p>}
          {rep && (
            <div data-testid="srcval-exception-report">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="font-mono text-[10px] text-slate-400 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">{rep.run_id}</span>
                <span className="text-[11px] text-slate-500">Run by {rep.run_by} · {new Date(rep.run_at).toLocaleString()}</span>
                {[["verified", rep.summary.verified, "text-emerald-700"], ["conflicts", rep.summary.conflicts, "text-red-700"],
                  ["missing fields", rep.summary.missing_fields, "text-amber-700"], ["reclassified", rep.summary.reclassified, "text-slate-700"],
                  ["synthesis approvals suspended", rep.summary.synthesis_approvals_suspended, "text-indigo-700"]].map(([l, v, c]) => (
                  <span key={l} className={`text-[10.5px] font-semibold ${c} bg-slate-50 border border-slate-200 rounded-full px-2.5 py-0.5`}>{v} {l}</span>
                ))}
              </div>
              <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1">Exception report — sources not objectively verified</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" aria-label="Source validation exception report">
                  <thead><tr className="text-left border-b border-slate-200">
                    {["Source", "Class", "Previous status", "New status", "Failed requirements"].map((h) => (
                      <th key={h} scope="col" className="py-1.5 pr-3 font-mono text-[9px] uppercase tracking-widest text-slate-400">{h}</th>))}
                  </tr></thead>
                  <tbody>
                    {rep.exceptions.map((x) => (
                      <tr key={x.source_id} className="border-b border-slate-100 align-top" data-testid={`srcval-exception-${x.source_id}`}>
                        <td className="py-2 pr-3 whitespace-nowrap"><span className="font-mono text-[10px] text-teal-700 font-semibold">{x.source_id}</span>
                          <p className="text-[10.5px] text-slate-600 max-w-[240px] leading-snug">{x.title}</p></td>
                        <td className="py-2 pr-3 text-slate-500 whitespace-nowrap">{x.validation_class}</td>
                        <td className="py-2 pr-3 text-slate-400 whitespace-nowrap">{x.previous_status}</td>
                        <td className="py-2 pr-3 whitespace-nowrap">
                          <Badge variant="outline" className={`text-[9.5px] ${OUTCOME_BG[x.outcome]}`}>{x.resulting_status}</Badge>
                          {x.synthesis_approval_suspended && <p className="text-[9.5px] text-indigo-600 mt-0.5">Synthesis approval suspended</p>}
                        </td>
                        <td className="py-2 pr-3 text-slate-600 max-w-md leading-snug">
                          {x.checks.filter((c) => c.status !== "Verified").map((c) => (
                            <p key={c.id} className="mb-0.5"><span className={`font-semibold ${c.status === "Conflict" ? "text-red-700" : "text-amber-700"}`}>{c.name}:</span> {c.detail}</p>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      {confirming && <ConfirmDialog onClose={() => setConfirming(false)} onConfirm={run} busy={busy} />}
    </div>
  );
};
